import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  UserManagementService,
  type OrganizationUser,
  type InviteUserData,
} from "../../services/user-management.service";
import { OrganisationService } from "../../services/organisation.service";
import { AuthService } from "../../services/auth.service.real";
import { ProfileService } from "../../services/profile.service";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Label } from "../ui/Label";
import { Modal } from "../ui/Modal";
import { SuccessMessage } from "../ui/SuccessMessage";
import { ErrorMessage } from "../ui/ErrorMessage";
import { HttpError } from "../../services/http.service";
import { getAssignableRoles, getRoleLabel, ROLES, isAdmin } from "../../utils/roles";

type SortField = "name" | "email" | "status" | "created";
type SortDirection = "asc" | "desc" | null;

type ActionModal =
  | { kind: "remove"; user: OrganizationUser }
  | { kind: "cancelInvitation"; user: OrganizationUser }
  | { kind: "leaveOrg" }
  | { kind: "toggleAdmin"; user: OrganizationUser; granting: boolean }
  | { kind: "transferAdmin"; candidates: OrganizationUser[] }
  | { kind: "deleteOrg" };

const inviteUserSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  roles: z.array(z.string()),
});

type InviteUserFormData = z.infer<typeof inviteUserSchema>;

export const ManageUsers: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [activeModal, setActiveModal] = useState<ActionModal | null>(null);
  const [selectedTransferUserId, setSelectedTransferUserId] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const organisation = OrganisationService.getOrganisationFromStorage();
  const organizationId = organisation?.id;

  const currentUser = AuthService.getCurrentUser();
  const currentUserProfile = ProfileService.getUserProfileFromStorage();
  const currentUserRoles = currentUserProfile?.roles || [];
  const assignableRoles = getAssignableRoles(currentUserRoles);
  const canManageMembers = isAdmin(currentUserRoles);

  const {
    data: users = [],
    isLoading,
    error: fetchError,
  } = useQuery({
    queryKey: ["organization-users", organizationId],
    queryFn: () => UserManagementService.getUsersByOrganization(organizationId!),
    enabled: !!organizationId,
  });

  const inviteMutation = useMutation({
    mutationFn: (data: InviteUserData) => UserManagementService.inviteUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization-users"] });
      setIsInviteModalOpen(false);
      setSuccessMessage("User invited successfully!");
      reset();
    },
    onError: (error) => {
      setErrorMessage(
        error instanceof HttpError ? error.message : "Failed to invite user. Please try again."
      );
    },
  });

  const removeMutation = useMutation({
    mutationFn: ({ userId }: { userId: string }) =>
      UserManagementService.removeMember(organizationId!, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization-users"] });
      setActiveModal(null);
      setSuccessMessage("User removed from the organisation.");
    },
    onError: handleMemberError,
  });

  const removeSelfMutation = useMutation({
    mutationFn: () => UserManagementService.removeSelf(organizationId!),
    onSuccess: async () => {
      setActiveModal(null);
      await AuthService.logout();
      navigate("/login");
    },
    onError: (error) => {
      if (error instanceof HttpError && error.data?.code === "last_admin") {
        // Determine whether there are other active members to hand off to
        const candidates = users.filter((u) => u.id !== currentUser?.id && u.status === "ACTIVE");
        if (candidates.length > 0) {
          setActiveModal({ kind: "transferAdmin", candidates });
          setSelectedTransferUserId(candidates[0].id);
        } else {
          setActiveModal({ kind: "deleteOrg" });
        }
      } else {
        handleMemberError(error);
      }
    },
  });

  // Grant admin to someone then leave — sequential, atomic from the user's perspective
  const transferAndLeaveMutation = useMutation({
    mutationFn: async ({ newAdminId }: { newAdminId: string }) => {
      await UserManagementService.grantRole(organizationId!, newAdminId, ROLES.ORG_ADMIN);
      await UserManagementService.removeSelf(organizationId!);
    },
    onSuccess: async () => {
      setActiveModal(null);
      await AuthService.logout();
      navigate("/login");
    },
    onError: handleMemberError,
  });

  const deleteOrgMutation = useMutation({
    mutationFn: () => OrganisationService.deleteOrganisation(organizationId!),
    onSuccess: async () => {
      setActiveModal(null);
      await AuthService.logout();
      navigate("/login");
    },
    onError: handleMemberError,
  });

  const cancelInvitationMutation = useMutation({
    mutationFn: ({ userId }: { userId: string }) =>
      UserManagementService.cancelInvitation(organizationId!, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization-users"] });
      setActiveModal(null);
      setSuccessMessage("Invitation cancelled.");
    },
    onError: handleMemberError,
  });

  const grantRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      UserManagementService.grantRole(organizationId!, userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization-users"] });
      setActiveModal(null);
      setSuccessMessage("Role granted successfully.");
    },
    onError: handleMemberError,
  });

  const revokeRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      UserManagementService.revokeRole(organizationId!, userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization-users"] });
      setActiveModal(null);
      setSuccessMessage("Role revoked successfully.");
    },
    onError: handleMemberError,
  });

  function handleMemberError(error: unknown) {
    setActiveModal(null);
    if (error instanceof HttpError) {
      setErrorMessage(error.message || "Operation failed. Please try again.");
    } else {
      setErrorMessage("Operation failed. Please try again.");
    }
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<InviteUserFormData>({
    resolver: zodResolver(inviteUserSchema),
    defaultValues: { email: "", roles: [] },
  });

  const onSubmitInvite = (data: InviteUserFormData) => inviteMutation.mutate(data);

  const getStatusBadge = (status: OrganizationUser["status"]) => {
    const styles: Record<string, string> = {
      ACTIVE: "bg-green-100 text-green-800",
      PENDING_ACTIVATION: "bg-yellow-100 text-yellow-800",
      INACTIVE: "bg-gray-100 text-gray-800",
    };
    return (
      <span
        className={`px-2 py-1 text-xs font-medium rounded ${styles[status] ?? styles.INACTIVE}`}
      >
        {status.replace(/_/g, " ")}
      </span>
    );
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  const formatRolesForDisplay = (roles: string[]): string[] => [
    "User",
    ...roles
      .filter(
        (role) => role === ROLES.ORG_ADMIN || (role.endsWith("_ADMIN") && role !== ROLES.ADMIN)
      )
      .map(getRoleLabel)
      .sort((a, b) => a.localeCompare(b)),
  ];

  const isCurrentUser = (user: OrganizationUser) => currentUser?.id === user.id;
  const isOrgAdmin = (user: OrganizationUser) => user.roles.includes(ROLES.ORG_ADMIN);

  const sortedUsers = useMemo(() => {
    // Default sorting: current user first, then by createdAt ascending
    if (!sortField || !sortDirection) {
      return [...users].sort((a, b) => {
        const aIsCurrent = currentUser?.id === a.id;
        const bIsCurrent = currentUser?.id === b.id;

        // Current user always comes first
        if (aIsCurrent && !bIsCurrent) return -1;
        if (!aIsCurrent && bIsCurrent) return 1;

        // Sort others by createdAt ascending
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      });
    }

    // Column sorting is active
    return [...users].sort((a, b) => {
      let av: string | number;
      let bv: string | number;
      if (sortField === "name") {
        av = `${a.firstName} ${a.lastName}`.toLowerCase();
        bv = `${b.firstName} ${b.lastName}`.toLowerCase();
      } else if (sortField === "email") {
        av = a.email.toLowerCase();
        bv = b.email.toLowerCase();
      } else if (sortField === "status") {
        av = a.status;
        bv = b.status;
      } else {
        av = new Date(a.createdAt).getTime();
        bv = new Date(b.createdAt).getTime();
      }
      if (sortDirection === "asc") return av > bv ? 1 : av < bv ? -1 : 0;
      return av < bv ? 1 : av > bv ? -1 : 0;
    });
  }, [users, sortField, sortDirection, currentUser]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === "asc") setSortDirection("desc");
      else {
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const SortIcon = ({ field }: { field: SortField }) =>
    sortField === field && sortDirection ? (
      <span className="ml-1">{sortDirection === "asc" ? "↑" : "↓"}</span>
    ) : null;

  const actionPending =
    removeMutation.isPending ||
    removeSelfMutation.isPending ||
    cancelInvitationMutation.isPending ||
    grantRoleMutation.isPending ||
    revokeRoleMutation.isPending ||
    transferAndLeaveMutation.isPending ||
    deleteOrgMutation.isPending;

  const handleConfirmAction = () => {
    if (!activeModal) return;
    switch (activeModal.kind) {
      case "remove":
        removeMutation.mutate({ userId: activeModal.user.id });
        break;
      case "cancelInvitation":
        cancelInvitationMutation.mutate({ userId: activeModal.user.id });
        break;
      case "leaveOrg":
        removeSelfMutation.mutate();
        break;
      case "toggleAdmin":
        if (activeModal.granting) {
          grantRoleMutation.mutate({ userId: activeModal.user.id, role: ROLES.ORG_ADMIN });
        } else {
          revokeRoleMutation.mutate({ userId: activeModal.user.id, role: ROLES.ORG_ADMIN });
        }
        break;
      case "transferAdmin":
        if (selectedTransferUserId) {
          transferAndLeaveMutation.mutate({ newAdminId: selectedTransferUserId });
        }
        break;
      case "deleteOrg":
        deleteOrgMutation.mutate();
        break;
    }
  };

  const renderActionButtons = (user: OrganizationUser) => {
    if (isCurrentUser(user)) {
      return (
        <button
          onClick={() => setActiveModal({ kind: "leaveOrg" })}
          className="text-xs text-gray-500 hover:text-red-600 underline"
        >
          Leave org
        </button>
      );
    }

    if (!canManageMembers) return null;

    if (user.status === "PENDING_ACTIVATION") {
      return (
        <button
          onClick={() => setActiveModal({ kind: "cancelInvitation", user })}
          className="text-xs text-yellow-700 hover:text-yellow-900 underline"
        >
          Cancel invitation
        </button>
      );
    }

    return (
      <div className="flex flex-col gap-1">
        <button
          onClick={() => setActiveModal({ kind: "toggleAdmin", user, granting: !isOrgAdmin(user) })}
          className={`text-xs underline ${
            isOrgAdmin(user)
              ? "text-orange-600 hover:text-orange-800"
              : "text-primary-blue hover:text-blue-800"
          }`}
        >
          {isOrgAdmin(user) ? "Revoke admin" : "Make admin"}
        </button>
        <button
          onClick={() => setActiveModal({ kind: "remove", user })}
          className="text-xs text-red-600 hover:text-red-800 underline"
        >
          Remove
        </button>
      </div>
    );
  };

  // Returns config for all simple confirmation modals
  type ModalConfig = {
    title: string;
    body: React.ReactNode;
    confirmLabel: string;
    confirmClass: string;
    confirmDisabled?: boolean;
  };

  const modalConfig: ModalConfig | null = (() => {
    if (!activeModal) return null;

    if (activeModal.kind === "remove")
      return {
        title: "Remove Member",
        body: (
          <>
            <p className="text-sm text-gray-700">
              Remove{" "}
              <strong>
                {activeModal.user.firstName} {activeModal.user.lastName}
              </strong>{" "}
              ({activeModal.user.email}) from the organisation?
            </p>
            <p className="text-sm text-gray-500 mt-2">Their access will be revoked immediately.</p>
          </>
        ),
        confirmLabel: "Remove",
        confirmClass: "bg-red-600 hover:bg-red-700 text-white",
      };

    if (activeModal.kind === "cancelInvitation")
      return {
        title: "Cancel Invitation",
        body: (
          <p className="text-sm text-gray-700">
            Cancel the pending invitation for <strong>{activeModal.user.email}</strong>?
          </p>
        ),
        confirmLabel: "Cancel invitation",
        confirmClass: "bg-red-600 hover:bg-red-700 text-white",
      };

    if (activeModal.kind === "leaveOrg")
      return {
        title: "Leave Organisation",
        body: (
          <p className="text-sm text-gray-700">
            Are you sure you want to leave this organisation? You will lose access immediately.
          </p>
        ),
        confirmLabel: "Leave",
        confirmClass: "bg-red-600 hover:bg-red-700 text-white",
      };

    if (activeModal.kind === "toggleAdmin")
      return {
        title: activeModal.granting ? "Grant Admin Role" : "Revoke Admin Role",
        body: (
          <p className="text-sm text-gray-700">
            {activeModal.granting ? "Grant" : "Revoke"} Organisation Admin role for{" "}
            <strong>
              {activeModal.user.firstName} {activeModal.user.lastName}
            </strong>
            ?
            {!activeModal.granting && (
              <span className="block mt-2 text-xs text-gray-500">
                Their session will be invalidated and they will need to log in again.
              </span>
            )}
          </p>
        ),
        confirmLabel: activeModal.granting ? "Grant" : "Revoke",
        confirmClass: "bg-primary-blue hover:bg-primary-blue/90 text-white",
      };

    if (activeModal.kind === "transferAdmin")
      return {
        title: "Transfer Admin & Leave",
        body: (
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              You are the only admin. Choose a member to promote before leaving:
            </p>
            <div className="space-y-2 max-h-48 overflow-y-auto border border-border-gray rounded p-2">
              {activeModal.candidates.map((candidate) => (
                <label
                  key={candidate.id}
                  className={`flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-gray-50 ${
                    selectedTransferUserId === candidate.id ? "bg-blue-50" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="transferUser"
                    value={candidate.id}
                    checked={selectedTransferUserId === candidate.id}
                    onChange={() => setSelectedTransferUserId(candidate.id)}
                    className="text-primary-blue focus:ring-primary-blue"
                  />
                  <div>
                    <p className="text-sm font-medium">
                      {candidate.firstName} {candidate.lastName}
                    </p>
                    <p className="text-xs text-gray-500">{candidate.email}</p>
                  </div>
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-500">
              They will receive admin rights and you will be removed from the organisation.
            </p>
          </div>
        ),
        confirmLabel: "Transfer & Leave",
        confirmClass: "bg-primary-blue hover:bg-primary-blue/90 text-white",
        confirmDisabled: !selectedTransferUserId,
      };

    if (activeModal.kind === "deleteOrg")
      return {
        title: "Delete Organisation",
        body: (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              You are the only member of this organisation. Since there is no one to transfer admin
              rights to, the organisation will be <strong>permanently deleted</strong> along with
              your membership.
            </p>
            <p className="text-sm text-red-600 font-medium">This action cannot be undone.</p>
          </div>
        ),
        confirmLabel: "Delete Organisation",
        confirmClass: "bg-red-600 hover:bg-red-700 text-white",
      };

    return null;
  })();

  if (!organizationId) {
    return (
      <div className="bg-white border border-border-gray p-6">
        <p className="text-red-600">Organization information not found.</p>
      </div>
    );
  }

  const thClass =
    "px-3 xl:px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase border-b-2 border-border-gray cursor-pointer hover:bg-gray-100 select-none whitespace-nowrap";
  const thStaticClass =
    "px-3 xl:px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase border-b-2 border-border-gray whitespace-nowrap";

  return (
    <div className="bg-white border border-border-gray p-4 sm:p-6">
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-base font-bold uppercase tracking-wide">MANAGE USERS</h3>
          <p className="text-sm text-gray-600 mt-2">
            View, invite, and manage users in your organization
          </p>
        </div>
        {canManageMembers && (
          <Button
            onClick={() => setIsInviteModalOpen(true)}
            className="px-6 h-10 bg-primary-blue hover:bg-primary-blue/90 text-white text-sm whitespace-nowrap"
          >
            Invite User
          </Button>
        )}
      </div>

      {successMessage && (
        <SuccessMessage
          message={successMessage}
          onClose={() => setSuccessMessage("")}
          className="mb-6"
        />
      )}
      {errorMessage && (
        <ErrorMessage
          message={errorMessage}
          onDismiss={() => setErrorMessage("")}
          className="mb-6"
        />
      )}
      {fetchError && (
        <ErrorMessage
          message="Failed to load users. Please try again."
          onDismiss={() => {}}
          className="mb-6"
        />
      )}

      {isLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin h-8 w-8 border-b-2 border-primary-blue mx-auto" />
          <p className="mt-4 text-gray-600">Loading users...</p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full border-2 border-border-gray table-auto">
              <thead className="bg-gray-50">
                <tr>
                  <th className={thClass} onClick={() => handleSort("name")}>
                    <div className="flex items-center justify-between">
                      <span>Name</span>
                      <SortIcon field="name" />
                    </div>
                  </th>
                  <th className={thClass} onClick={() => handleSort("email")}>
                    <div className="flex items-center justify-between">
                      <span>Email</span>
                      <SortIcon field="email" />
                    </div>
                  </th>
                  <th className={`${thClass} text-center`} onClick={() => handleSort("status")}>
                    <div className="flex items-center justify-center">
                      <span>Status</span>
                      <SortIcon field="status" />
                    </div>
                  </th>
                  <th className={thStaticClass}>Roles</th>
                  <th className={thClass} onClick={() => handleSort("created")}>
                    <div className="flex items-center justify-between">
                      <span>Created</span>
                      <SortIcon field="created" />
                    </div>
                  </th>
                  <th className={`${thStaticClass} text-center`}>Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {sortedUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 xl:px-4 py-8 text-center text-gray-500">
                      No users found in this organization.
                    </td>
                  </tr>
                ) : (
                  sortedUsers.map((user) => (
                    <tr
                      key={user.id}
                      className={`border-b border-border-gray hover:bg-gray-50 ${
                        isCurrentUser(user) ? "bg-blue-50/40" : ""
                      }`}
                    >
                      <td className="px-3 xl:px-4 py-3 text-sm whitespace-nowrap">
                        {user.firstName} {user.lastName}
                        {isCurrentUser(user) && (
                          <span className="ml-2 text-xs text-gray-400">(you)</span>
                        )}
                      </td>
                      <td className="px-3 xl:px-4 py-3 text-sm break-words min-w-[150px] max-w-[270px]">
                        {user.email}
                      </td>
                      <td className="px-3 xl:px-4 py-3 text-sm">{getStatusBadge(user.status)}</td>
                      <td className="px-3 xl:px-4 py-3 text-sm">
                        <ul className="list-disc list-inside break-words min-w-[150px] max-w-[270px] max-h-[100px] overflow-y-auto">
                          {formatRolesForDisplay(user.roles).map((role, index) => (
                            <li key={index}>{role}</li>
                          ))}
                        </ul>
                      </td>
                      <td className="px-3 xl:px-4 py-3 text-sm whitespace-nowrap">
                        {formatDate(user.createdAt)}
                      </td>
                      <td className="px-3 xl:px-4 py-3 text-sm">
                        <div className="flex items-center justify-center">
                          {renderActionButtons(user)}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="lg:hidden space-y-4">
            {sortedUsers.length === 0 ? (
              <div className="text-center py-8 text-gray-500 border-2 border-border-gray bg-white">
                No users found in this organization.
              </div>
            ) : (
              sortedUsers.map((user) => (
                <div
                  key={user.id}
                  className={`border-2 border-border-gray bg-white p-4 space-y-3 ${
                    isCurrentUser(user) ? "bg-blue-50/40" : ""
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-semibold text-sm">
                        {user.firstName} {user.lastName}
                        {isCurrentUser(user) && (
                          <span className="ml-2 text-xs text-gray-400">(you)</span>
                        )}
                      </h4>
                      <p className="text-xs text-gray-600 mt-1">{user.email}</p>
                    </div>
                    {getStatusBadge(user.status)}
                  </div>

                  <div>
                    <p className="text-xs text-gray-600 mb-1">Roles:</p>
                    <ul className="list-disc list-inside text-sm max-h-[120px] overflow-y-auto">
                      {formatRolesForDisplay(user.roles).map((role, index) => (
                        <li key={index}>{role}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex justify-between items-center pt-2 border-t border-border-gray">
                    <span className="text-xs text-gray-600">
                      Created: {formatDate(user.createdAt)}
                    </span>
                    <div className="flex items-center justify-center">
                      {renderActionButtons(user)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* Invite User Modal */}
      <Modal
        isOpen={isInviteModalOpen}
        onClose={() => {
          setIsInviteModalOpen(false);
          reset();
        }}
        title="Invite New User"
      >
        <form onSubmit={handleSubmit(onSubmitInvite)} className="space-y-6">
          <div>
            <Label htmlFor="email" className="block text-sm font-medium text-black mb-2">
              Email address
            </Label>
            <Input
              id="email"
              type="email"
              {...register("email")}
              error={!!errors.email}
              className="w-full h-12 bg-disabled-bg border-border-gray"
              placeholder="user@example.com"
            />
            {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>}
          </div>

          <div>
            <Label className="block text-sm font-medium text-black mb-2">Roles</Label>
            {assignableRoles.length === 0 ? (
              <p className="text-sm text-gray-600">
                You don't have permission to assign any roles.
              </p>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                <label className="flex items-start space-x-2">
                  <input
                    type="checkbox"
                    checked
                    disabled
                    className="h-4 w-4 mt-0.5 text-primary-blue focus:ring-primary-blue border-gray-300"
                  />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">User</span>
                    <span className="text-xs text-gray-600">
                      Basic platform access (always assigned)
                    </span>
                  </div>
                </label>
                {assignableRoles.map((role) => (
                  <label key={role.value} className="flex items-start space-x-2">
                    <input
                      type="checkbox"
                      value={role.value}
                      {...register("roles")}
                      className="h-4 w-4 mt-0.5 text-primary-blue focus:ring-primary-blue border-gray-300"
                    />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">{role.label}</span>
                      <span className="text-xs text-gray-600">{role.description}</span>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsInviteModalOpen(false);
                reset();
              }}
              disabled={inviteMutation.isPending}
              className="px-6 h-12 border-border-gray text-black hover:bg-gray-50"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={inviteMutation.isPending}
              className="px-6 h-12 bg-primary-blue hover:bg-primary-blue/90 text-white"
            >
              {inviteMutation.isPending ? "Inviting..." : "Send Invitation"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Generic confirmation modal for all actions */}
      {modalConfig && (
        <Modal
          isOpen={activeModal !== null}
          onClose={() => setActiveModal(null)}
          title={modalConfig.title}
          className="max-w-md"
        >
          <div className="space-y-4">
            {modalConfig.body}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setActiveModal(null)}
                disabled={actionPending}
                className="px-6 h-12 border-border-gray text-black hover:bg-gray-50"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleConfirmAction}
                disabled={actionPending || !!modalConfig.confirmDisabled}
                className={`px-6 h-12 ${modalConfig.confirmClass}`}
              >
                {actionPending ? "Please wait..." : modalConfig.confirmLabel}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
